import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useSEO } from "@/lib/useSEO";
import { CarCard } from "@/components/site/CarCard";
import { Search } from "lucide-react";

export default function Catalog({ listingType }) {
  useSEO({
    title: listingType === "rent" ? "Αυτοκίνητα προς Ενοικίαση" : "Αυτοκίνητα προς Πώληση",
    description: listingType === "rent"
      ? "Premium ημερήσιες ενοικιάσεις από τον ιδιωτικό μας στόλο πολυτελών αυτοκινήτων."
      : "Επιλεγμένη συλλογή πολυτελών αυτοκινήτων προς αγορά. Mercedes-Benz, BMW, Porsche και άλλες κορυφαίες μάρκες.",
    path: listingType === "rent" ? "/rent" : "/buy",
  });
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: "",
    make: "",
    body_type: "",
    max_price: "",
  });

  const fetchCars = async () => {
    setLoading(true);
    const params = { listing_type: listingType };
    if (filters.search) params.search = filters.search;
    if (filters.make) params.make = filters.make;
    if (filters.body_type) params.body_type = filters.body_type;
    if (filters.max_price) params.max_price = filters.max_price;
    try {
      const { data } = await api.get("/cars", { params });
      setCars(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCars(); /* eslint-disable-next-line */ }, [listingType]);

  const headline = listingType === "rent" ? "Αυτοκίνητα προς Ενοικίαση" : "Αυτοκίνητα προς Πώληση";
  const subtitle = listingType === "rent"
    ? "Premium ημερήσιες ενοικιάσεις από τον ιδιωτικό μας στόλο."
    : "Επιλεγμένη συλλογή πολυτελών αυτοκινήτων έτοιμων προς αγορά.";

  return (
    <div data-testid={`catalog-${listingType}`} className="pt-32 pb-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-12">
          <div className="label-caps mb-4">{listingType === "rent" ? "Στόλος Ενοικίασης" : "Συλλογή"}</div>
          <h1 className="font-display text-4xl sm:text-6xl font-extrabold tracking-tight uppercase mb-4">
            {headline}
          </h1>
          <p className="text-lg text-gray-400 max-w-2xl font-light italic">{subtitle}</p>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 pb-10 border-b border-white/5 mb-12">
          <div className="md:col-span-2 relative">
            <Search size={16} className="absolute left-0 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              data-testid="filter-search"
              type="text"
              placeholder="Αναζήτηση μάρκας ή μοντέλου..."
              className="input-line pl-8"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && fetchCars()}
            />
          </div>
          <select
            data-testid="filter-make"
            className="select-dark"
            value={filters.make}
            onChange={(e) => setFilters({ ...filters, make: e.target.value })}
          >
            <option value="">Όλες οι Μάρκες</option>
            {["Porsche", "Mercedes-Benz", "BMW", "Audi", "Lamborghini", "Land Rover", "Range Rover"].map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
          <select
            data-testid="filter-body"
            className="select-dark"
            value={filters.body_type}
            onChange={(e) => setFilters({ ...filters, body_type: e.target.value })}
          >
            <option value="">Όλοι οι Τύποι</option>
            {["Sedan", "Coupe", "SUV", "Convertible", "Coupe-Sport"].map((b) => (
              <option key={b} value={b}>{b}</option>
            ))}
          </select>
          <button onClick={fetchCars} data-testid="filter-apply" className="btn-primary">
            Εφαρμογή
          </button>
        </div>

        {/* Results */}
        {loading ? (
          <div className="text-center py-24 text-gray-500 tracking-widest uppercase text-xs font-display">Φόρτωση...</div>
        ) : cars.length === 0 ? (
          <div className="text-center py-24 text-gray-500 tracking-widest uppercase text-xs font-display">Κανένα όχημα δεν ταιριάζει με τα κριτήριά σας.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {cars.map((c, i) => <CarCard key={c.id} car={c} index={i} />)}
          </div>
        )}
      </div>
    </div>
  );
}
